import React from 'react';
import { List, ListItem, ListItemText } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import CheckIcon from '@material-ui/icons/Check';
import DateRangeIcon from '@material-ui/icons/DateRange';
import WatchLaterIcon from '@material-ui/icons/WatchLater';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import PaymentIcon from '@material-ui/icons/Payment';
import { useTranslation } from 'react-i18next';
import { FullHeightBox, ListItemIconDense, SmallAvatar, SquareCard, TableIcon } from '../../../../ui-kit';
import { getFullName, getInitials } from '../../../../utils';
import { useDateTimeService } from '../../../../utils/hooks/dateTimeService';
import { useUser } from '../../../../utils/hooks/auth';
import { BillQuery, BillState } from '../../../../generated/graphql';
import { BillAmount } from './BillAmount';

const BillSidebar = ({ bill }: BillQuery) => {
    const { dateTimeService } = useDateTimeService();
    const { user } = useUser();
    const { t } = useTranslation();

    const {
        issuedBy: { account: { firstName, lastName, imageUrl } },
        issuedUtc,
        paymentMethod,
        customPaymentMethod,
        place,
        total,
        billState,
        dicountedTotalValue,
        roundedPrice,
    } = bill;

    const isVoided = billState === BillState.Voided;

    const dateString = dateTimeService.localizeDate(issuedUtc, user.cultureCode);
    const timeString = dateTimeService.localizeTime(issuedUtc, user.cultureCode);

    const fullName = getFullName({ firstName, lastName });
    const initials = getInitials({ firstName, lastName });

    const placeName = place ? place.name : t('Take away');

    return (
        <FullHeightBox component={SquareCard}>
            <List dense>
                <ListItem>
                    <ListItemIconDense>
                        <TableIcon />
                    </ListItemIconDense>
                    <ListItemText primary={placeName} />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        <DateRangeIcon />
                    </ListItemIconDense>
                    <ListItemText primary={dateString} />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        <WatchLaterIcon />
                    </ListItemIconDense>
                    <ListItemText primary={timeString} />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        <MonetizationOnIcon />
                    </ListItemIconDense>
                    <ListItemText
                        primary={(
                            <BillAmount
                                total={total}
                                dicountedTotalValue={dicountedTotalValue}
                                roundedPrice={roundedPrice}
                            />
                        )}
                    />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        <PaymentIcon />
                    </ListItemIconDense>
                    <ListItemText primary={customPaymentMethod ? customPaymentMethod.name : t(paymentMethod)} />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        {isVoided ? <WarningIcon color='error' /> : <CheckIcon />}
                    </ListItemIconDense>
                    <ListItemText primary={t(billState)} />
                </ListItem>
                <ListItem>
                    <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary={t('Issued by')} />
                </ListItem>
                <ListItem>
                    <ListItemIconDense>
                        <SmallAvatar src={imageUrl || undefined}>{initials}</SmallAvatar>
                    </ListItemIconDense>
                    <ListItemText primary={fullName} />
                </ListItem>
            </List>
        </FullHeightBox>
    );
};

export default BillSidebar;
